/**
 * googleOAuth — the ONE way to start Google OAuth from the browser.
 *
 * 2026-06-21. "Reconnect Google" returned `401 {"detail":"Not authenticated"}`
 * because it navigated to `/api/auth/google/login` WITHOUT the `token` query
 * param. `routes/google_auth.py` resolves identity from an `Authorization:
 * Bearer` header OR `?token=`, and a top-level `window.location` navigation
 * cannot carry a header — which is exactly what that endpoint's docstring says.
 *
 * There were SIX different implementations of this one navigation. Only two
 * (GoogleImportDialog, ContactSyncSettings) passed the token; two passed
 * `user_id` and no token (so they 401 like the reauth button did), and two used
 * `axios.get` expecting a JSON `authorization_url` from an endpoint that
 * answers with a 307 redirect. All six now call `startGoogleOAuth`.
 *
 * The JWT lives in the URL only for the duration of one same-origin navigation,
 * is short-lived, and Google never sees it — but it DOES reach browser history
 * and any access log, so the backend redacts `token=` from its logs
 * (server.py `_RedactSensitiveQueryParams`).
 */
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/** The login page stores the JWT here (LoginPage.js / JoinPage.js). */
export function getStoredJwt() {
  if (typeof window === 'undefined') return '';
  return window.localStorage.getItem('accessToken') || '';
}

/**
 * Start the Google consent flow with a top-level navigation.
 *
 * @param {object}  opts
 * @param {string}  opts.redirectPath  where to land after the callback
 *                                     (defaults to the current path)
 * @param {string}  opts.userId        optional; the endpoint prefers the JWT
 * @returns {boolean} false when the user is not signed in (nothing navigated)
 */
export function startGoogleOAuth({ redirectPath, userId } = {}) {
  const jwt = getStoredJwt();
  if (!jwt) {
    toast.error('You need to be signed in to connect Google.');
    return false;
  }
  const returnPath = redirectPath || (typeof window !== 'undefined' ? window.location.pathname : '/');
  const params = new URLSearchParams({
    redirect_url: returnPath,
    token: jwt,
  });
  if (userId) params.set('user_id', userId);
  window.location.href = `${API}/api/auth/google/login?${params.toString()}`;
  return true;
}

export default startGoogleOAuth;
